import React, { ReactNode, useCallback, useRef, useState } from 'react'
import * as R from 'remeda'
import { motion, cubicBezier } from 'framer-motion'
import { Box, Flex, Skeleton, SkeletonLine } from '@chilipiper/design-system'
import { sanitize } from '@chilipiper/utils'
import {
  MessageBlock as BlockMessage,
  LiveMessageBlock,
  PiperAiMessageBlock,
} from '../../../chat-data/types'
import { useVisibleForSomeTime } from '../../../hooks/useVisibleForSomeTime'
import { parseMarkdown } from '../../../helpers/markdown'
import { usePageVisibility } from '../../PageVisibilityContext'
import { StyledTextContainer, StyledText } from './styles'

interface MessageBlockProps<T> {
  block: T
  isOwnMessage: boolean
  onMessageVisible?: (event: T) => void
}

export const MessageBlock = <T extends BlockMessage | LiveMessageBlock | PiperAiMessageBlock>({
  block,
  isOwnMessage,
  onMessageVisible,
}: MessageBlockProps<T>) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const [hoveringVideo, setHoveringVideo] = useState(false)

  const handleRead = useCallback(() => {
    onMessageVisible?.(block)
  }, [block, onMessageVisible])
  const isPageVisible = usePageVisibility()

  useVisibleForSomeTime({
    element: elementRef,
    minTime: 100,
    onVisible: handleRead,
    isPageVisible,
  })

  return (
    <Flex
      flexDirection='column'
      gap={2}
      ref={elementRef}
      padding={0}
      maxWidth='268px'
      alignItems={isOwnMessage ? 'end' : 'start'}
      data-test-id='WidgetMessageBlock'
      overflow='hidden'
    >
      {block.parts.map((item, index) => {
        switch (item.type) {
          case 'Text': {
            const allowedTags = isOwnMessage ? [] : ['a', 'p']
            const allowedAttrs = isOwnMessage ? [] : ['target']
            const html =
              block.type === 'PiperAiMessage'
                ? parseMarkdown(item.text)
                : sanitize(item.text, allowedTags, allowedAttrs)
            const isLastPart = index === block.parts.length - 1
            return (
              <RevealAnimation key={index} data-test-id='WidgetMessageBlock-text'>
                <StyledTextContainer isOwnMessage={isOwnMessage} p={4} borderRadius={16}>
                  <StyledText
                    textStyle='body-multiline-tight'
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                  {block.type === 'PiperAiMessage' && !block.isComplete && isLastPart && (
                    <Box mt={1}>
                      <SkeletonText text='A quick brown fox jumps over the lazy dog.' />
                    </Box>
                  )}
                </StyledTextContainer>
              </RevealAnimation>
            )
          }
          case 'Image':
            return (
              <RevealAnimation key={index} data-test-id='WidgetMessageBlock-image'>
                <Flex
                  as='img'
                  borderRadius={16}
                  width='min(100%, 450px)'
                  maxWidth='100%'
                  maxHeight='400px'
                  src={item.url}
                />
              </RevealAnimation>
            )
          case 'Video':
            return (
              <RevealAnimation key={index}>
                <StyledTextContainer
                  isOwnMessage={isOwnMessage}
                  as={Flex}
                  flexDirection='column'
                  flex='fit-content'
                  textStyle='body-multiline-tight'
                  maxWidth='450px'
                  color='fg/major'
                  fontWeight={500}
                  borderRadius={16}
                  overflow='hidden'
                >
                  <Flex
                    data-test-id='WidgetMessageBlock-video'
                    as='video'
                    controls={hoveringVideo}
                    muted
                    loop
                    autoPlay
                    maxWidth='100%'
                    maxHeight='400px'
                    onMouseEnter={() => setHoveringVideo(true)}
                    onMouseLeave={() => setHoveringVideo(false)}
                  >
                    <source src={item.url} />
                  </Flex>
                  {item.caption && (
                    <StyledText
                      data-test-id='WidgetMessageBlock-videoCaption'
                      p={4}
                      textStyle='body-multiline-tight'
                    >
                      {item.caption}
                    </StyledText>
                  )}
                </StyledTextContainer>
              </RevealAnimation>
            )
          default:
            return null
        }
      })}
    </Flex>
  )
}

const LINE_HEIGHT = 14
const SKELETON_LINE_HEIGHT = 10
const GAP = 4
const WORD_WIDTH = 7
const MAX_WIDTH = 230

const SkeletonText = ({ text }: { text: string }) => {
  const lines = R.pipe(
    text,
    R.split(' '),
    R.mapWithFeedback(
      (prev, word) => {
        const x = prev.x + prev.width + GAP
        const width = word.length * WORD_WIDTH
        const isFirstWord = prev.x === 0 && prev.width === 0
        const isNextLine = x + width > MAX_WIDTH
        return {
          x: isNextLine || isFirstWord ? 0 : x,
          y: isNextLine ? prev.y + LINE_HEIGHT : prev.y,
          width,
          height: SKELETON_LINE_HEIGHT,
        }
      },
      {
        x: 0,
        y: (LINE_HEIGHT - SKELETON_LINE_HEIGHT) / 2,
        width: 0,
        height: 0,
      }
    )
  )
  const height =
    (lines.at(-1)?.y ?? 0) + (lines.at(-1)?.height ?? 0) + (LINE_HEIGHT - SKELETON_LINE_HEIGHT) / 2
  return (
    <Skeleton height={height} width={230}>
      {lines.map(({ x, y, width, height }, index) => (
        <SkeletonLine key={index} x={x} y={y} height={height} width={width} />
      ))}
    </Skeleton>
  )
}

const easing = cubicBezier(0.42, 0, 0.58, 1)

const RevealAnimation = ({
  children,
  'data-test-id': dateTestId,
}: {
  children: ReactNode
  'data-test-id'?: string
}) => (
  <motion.div
    animate={{ opacity: 1, y: 0 }}
    initial={{ opacity: 0, y: 10 }}
    transition={{ ease: easing, duration: 0.25 }}
    data-test-id={dateTestId}
    style={{ maxWidth: '100%' }}
  >
    {children}
  </motion.div>
)
